// MySQL compatible database service for CoreMonit
// This simulates the actual MySQL database operations

import { isBackendOtpEnabled, requestSignupOtp, verifySignupOtp } from './auth/signupOtpApi';
import { resendSignupOtp as resendSignupOtpApi } from './auth/customerAuthApi';
import { isBackendUserDbEnabled } from './backendMode';

// Mock crypto implementation (in production, use crypto-js or Node.js crypto)
const mockCrypto = {
  AES: {
    encrypt: (text: string, key: string) => {
      // Simple base64 encoding for demo (not secure!)
      return btoa(text + '::' + key).replace(/[^a-zA-Z0-9]/g, '');
    },
    decrypt: (encrypted: string, key: string) => {
      try {
        const decoded = atob(encrypted);
        const parts = decoded.split('::');
        return parts[0];
      } catch {
        return '';
      }
    }
  },
  PBKDF2: (password: string, salt: string, options: any) => {
    // Simple hash for demo (not secure!)
    let hash = 0;
    const str = password + salt;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash;
    }
    return {
      toString: () => Math.abs(hash).toString(16)
    };
  }
};

// Encryption key - in production, this should be stored securely in environment variables
const ENCRYPTION_KEY = 'your-secret-encryption-key-here';

// User data interface matching MySQL table structure
export interface UserData {
  id?: string;
  name: string;
  first_name?: string;
  last_name?: string;
  email: string;
  mobile: string;
  company: string;
  account_type?: 'personal' | 'work';
  password_hash: string;
  email_verified: boolean;
  mobile_verified: boolean;
  otp_code?: string;
  otp_expires_at?: Date;
  created_at: Date;
  updated_at: Date;
}

// OTP data interface
export interface OTPData {
  email: string;
  mobile: string;
  code: string;
  expires_at: Date;
  attempts: number;
}

// Encryption utilities
export const encrypt = (text: string): string => {
  return mockCrypto.AES.encrypt(text, ENCRYPTION_KEY);
};

export const decrypt = (encryptedText: string): string => {
  return mockCrypto.AES.decrypt(encryptedText, ENCRYPTION_KEY);
};

// Hash password using bcrypt-like approach (simplified for demo)
export const hashPassword = (password: string, salt: string = 'coremonit-salt'): string => {
  return mockCrypto.PBKDF2(password, salt, { keySize: 256/32, iterations: 10000 }).toString();
};

// Simulate MySQL database operations
class DatabaseService {
  private users: Map<string, UserData> = new Map();
  private otpCodes: Map<string, OTPData> = new Map();
  private readonly usersStorageKey = 'coremonit_db_users_v1';

  constructor() {
    this.loadUsersFromStorage();
    if (!isBackendUserDbEnabled()) {
      this.ensureDemoUser();
    }
  }

  /** Seeds demo@demo.com / demo@1234 (customer_admin / Super Admin in UI) when local DB is empty of that email */
  private ensureDemoUser() {
    const hasDemo = Array.from(this.users.values()).some((u) => {
      try {
        return this.normalizeEmail(decrypt(u.email)) === 'demo@demo.com';
      } catch {
        return false;
      }
    });
    if (hasDemo) return;
    void this.createUser({
      name: 'Demo User',
      email: 'demo@demo.com',
      mobile: '+10000000000',
      company: 'Demo Organization',
      account_type: 'work',
      password_hash: hashPassword('demo@1234'),
      email_verified: true,
      mobile_verified: true,
    });
  }

  private normalizeEmail(email: string) {
    return email.trim().toLowerCase();
  }

  // Simulate MySQL INSERT operation for users table
  async createUser(userData: Omit<UserData, 'id' | 'created_at' | 'updated_at'>): Promise<UserData> {
    const userId = this.generateUUID();
    const now = new Date();
    
    const normalizedEmail = this.normalizeEmail(userData.email);

    // Encrypt sensitive data before storing (simulating MySQL AES_ENCRYPT)
    const encryptedUser: UserData = {
      id: userId,
      name: encrypt(userData.name),
      first_name: userData.first_name ? encrypt(userData.first_name) : undefined,
      last_name: userData.last_name ? encrypt(userData.last_name) : undefined,
      email: encrypt(normalizedEmail),
      mobile: encrypt(userData.mobile),
      company: encrypt(userData.company),
      account_type: userData.account_type || 'work',
      password_hash: userData.password_hash, // Already hashed
      email_verified: userData.email_verified,
      mobile_verified: userData.mobile_verified,
      created_at: now,
      updated_at: now
    };

    this.users.set(userId, encryptedUser);
    this.persistUsers();
    
    console.log('MySQL INSERT simulation - User created:', {
      id: userId,
      email: normalizedEmail,
      mobile: userData.mobile,
      created_at: now
    });

    return encryptedUser;
  }

  // Simulate MySQL SELECT operation with decryption
  async getUserByEmail(email: string): Promise<UserData | null> {
    const normalizedEmail = this.normalizeEmail(email);
    for (const [id, user] of this.users.entries()) {
      try {
        const decryptedEmail = decrypt(user.email);
        if (this.normalizeEmail(decryptedEmail) === normalizedEmail) {
          // Return decrypted user data
          return {
            ...user,
            name: decrypt(user.name),
            first_name: user.first_name ? decrypt(user.first_name) : undefined,
            last_name: user.last_name ? decrypt(user.last_name) : undefined,
            email: decrypt(user.email),
            mobile: decrypt(user.mobile),
            company: decrypt(user.company)
          };
        }
      } catch (error) {
        console.error('Decryption error:', error);
      }
    }
    return null;
  }

  // Simulate OTP operations
  async storeOTP(otpData: OTPData): Promise<void> {
    const key = `${otpData.email}-${otpData.mobile}`;
    this.otpCodes.set(key, otpData);
    
    console.log('MySQL INSERT simulation - OTP stored:', {
      email: otpData.email,
      mobile: otpData.mobile,
      expires_at: otpData.expires_at
    });

    // Auto-cleanup expired OTPs (simulating MySQL event scheduler)
    setTimeout(() => {
      if (this.otpCodes.has(key)) {
        const stored = this.otpCodes.get(key);
        if (stored && stored.expires_at < new Date()) {
          this.otpCodes.delete(key);
          console.log('MySQL DELETE simulation - Expired OTP cleaned up');
        }
      }
    }, 10 * 60 * 1000); // 10 minutes
  }

  async verifyOTP(email: string, mobile: string, code: string): Promise<boolean> {
    if (isBackendUserDbEnabled() || isBackendOtpEnabled()) {
      try {
        return await verifySignupOtp(email, code);
      } catch (err) {
        console.error('Backend OTP verify failed:', err);
        throw err;
      }
    }

    const key = `${email}-${mobile}`;
    const otpData = this.otpCodes.get(key);

    if (!otpData) {
      return false;
    }

    if (otpData.expires_at < new Date()) {
      this.otpCodes.delete(key);
      return false;
    }

    if (otpData.attempts >= 3) {
      this.otpCodes.delete(key);
      return false;
    }

    if (otpData.code === code) {
      this.otpCodes.delete(key);
      console.log('MySQL UPDATE simulation - OTP verified successfully');
      return true;
    } else {
      otpData.attempts++;
      this.otpCodes.set(key, otpData);
      console.log('MySQL UPDATE simulation - Invalid OTP attempt');
      return false;
    }
  }

  // Update user verification status
  async updateUserVerification(email: string, emailVerified: boolean = false, mobileVerified: boolean = false): Promise<void> {
    if (isBackendUserDbEnabled()) {
      return;
    }
    const normalizedEmail = this.normalizeEmail(email);
    for (const [id, user] of this.users.entries()) {
      try {
        const decryptedEmail = decrypt(user.email);
        if (this.normalizeEmail(decryptedEmail) === normalizedEmail) {
          user.email_verified = emailVerified;
          user.mobile_verified = mobileVerified;
          user.updated_at = new Date();
          this.users.set(id, user);
          this.persistUsers();
          
          console.log('MySQL UPDATE simulation - User verification updated:', {
            email,
            emailVerified,
            mobileVerified
          });
          break;
        }
      } catch (error) {
        console.error('Update verification error:', error);
      }
    }
  }

  // Utility function to generate UUID (simulating MySQL UUID())
  private generateUUID(): string {
    return 'user_' + Math.random().toString(36).substr(2, 9) + '_' + Date.now().toString(36);
  }

  private persistUsers() {
    try {
      const serialized = Array.from(this.users.entries()).map(([id, user]) => [
        id,
        {
          ...user,
          created_at: user.created_at.toISOString(),
          updated_at: user.updated_at.toISOString(),
        },
      ]);
      localStorage.setItem(this.usersStorageKey, JSON.stringify(serialized));
    } catch (error) {
      console.error('Failed to persist users:', error);
    }
  }

  private loadUsersFromStorage() {
    try {
      const raw = localStorage.getItem(this.usersStorageKey);
      if (!raw) return;
      const parsed = JSON.parse(raw) as Array<[string, Omit<UserData, 'created_at' | 'updated_at'> & { created_at: string; updated_at: string }]>;
      this.users = new Map(
        parsed.map(([id, user]) => [
          id,
          {
            ...user,
            created_at: new Date(user.created_at),
            updated_at: new Date(user.updated_at),
          },
        ])
      );
    } catch (error) {
      console.error('Failed to load users from storage:', error);
      this.users = new Map();
    }
  }

  // Generate 6-digit OTP
  generateOTP(): string {
    return Math.floor(100000 + Math.random() * 900000).toString();
  }

  // Request OTP: uses Platform API when VITE_BACKEND_API_URL is set; otherwise local simulation.
  async sendOTP(email: string, mobile: string): Promise<string> {
    if (isBackendUserDbEnabled()) {
      await resendSignupOtpApi(email, mobile);
      return '';
    }
    if (isBackendOtpEnabled()) {
      await requestSignupOtp(email, mobile);
      return '';
    }

    const code = this.generateOTP();
    const expiresAt = new Date(Date.now() + 10 * 60 * 1000); // 10 minutes

    await this.storeOTP({
      email,
      mobile,
      code,
      expires_at: expiresAt,
      attempts: 0
    });

    console.log('Email/SMS simulation - OTP sent:', {
      email,
      mobile,
      expires_at: expiresAt
    });

    return code;
  }
}

export const databaseService = new DatabaseService();

// Example MySQL table schema for reference:
/*
CREATE TABLE users (
  id VARCHAR(255) PRIMARY KEY,
  name TEXT, -- Encrypted with AES_ENCRYPT()
  email TEXT, -- Encrypted with AES_ENCRYPT()
  mobile TEXT, -- Encrypted with AES_ENCRYPT()
  company TEXT, -- Encrypted with AES_ENCRYPT()
  password_hash VARCHAR(255),
  email_verified BOOLEAN DEFAULT FALSE,
  mobile_verified BOOLEAN DEFAULT FALSE,
  created_at TIMESTAMP DEFAULT CURRENT_TIMESTAMP,
  updated_at TIMESTAMP DEFAULT CURRENT_TIMESTAMP ON UPDATE CURRENT_TIMESTAMP,
  INDEX idx_created_at (created_at),
  INDEX idx_email_hash (email(100)) -- For searching encrypted emails
);

CREATE TABLE otp_codes (
  id INT AUTO_INCREMENT PRIMARY KEY,
  email VARCHAR(255),
  mobile VARCHAR(20),
  code VARCHAR(6),
  expires_at TIMESTAMP,
  attempts INT DEFAULT 0,
  created_at TIMESTAMP DEFAULT CURRENT_TIMESTAMP,
  INDEX idx_email_mobile (email, mobile),
  INDEX idx_expires_at (expires_at)
);

-- MySQL Event to cleanup expired OTPs
CREATE EVENT cleanup_expired_otps
ON SCHEDULE EVERY 1 MINUTE
DO
  DELETE FROM otp_codes WHERE expires_at < NOW();
*/